import React, { Fragment, useEffect, useRef } from "react";
import { useEffectOnce } from "../../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";

import { addNotification } from "../../../redux/slices/notificationsslice";
import {
  setCurrentPageIsDirty,
  setCurrentPage,
} from "../../../redux/slices/currentpageslice";
import { notificationDurationMiddle } from "../../../lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import { Page } from "../../page";
import { deepCompare } from "../../../lib/utils";
import { BLOCK_DIRTY_MESSAGE } from "../../../settings";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

import {
  setProcessing,
  disposePage,
  loadPage,
  setFormValue,
  setPageValue,
  stepFilePages,
  setPanes,
} from "../../../redux/slices/importdatasetpageslice";
import { ImportDatasetPageBody } from "./importdatasetpagebody";
import { ROUTING_PATHS } from "../../../settings";
import { dataSourceMaxReached } from "../../../routing/authorizationhandlers";

export const ImportDatasetPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.importDatasetPage);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPageState = useSelector((state) => state.currentPage);
  const { t, i18n } = useTranslation();

  let currentPageId = "importDatasetPage";

  const fromFileSteps = [
    { label: t("datasets.importdataset.from"), isValid: true },
    { label: t("datasets.importdataset.fromfile"), isValid: undefined },
    { label: t("datasets.importdataset.review"), isValid: undefined },
    { label: t("datasets.importdataset.result"), isValid: undefined },
  ];

  useEffect(() => {
    // If the current location is "/importdata" with query parameters, redirect to "/importdataset"
    if (location.pathname === "/importdata") {
      const searchParams = new URLSearchParams(location.search);
      const datasetId = searchParams.get("datasetid");

      // Redirect to the correct route with or without query params
      if (datasetId) {
        navigate(
          `${ROUTING_PATHS.importDataset}?datasetid=${datasetId}&from=file`,
          { replace: true }
        );
      } else {
        navigate(ROUTING_PATHS.importDataset, { replace: true });
      }
    }
  }, [location, navigate]);

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    let isDirty = !deepCompare(
      pageState.formInitialValues,
      pageState.formValues
    );
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  /*initial page load*/
  useEffectOnce(() => {
    const searchParams = new URLSearchParams(location.search);
    const datasetId = searchParams.get("datasetid");
    dispatch(loadPage(t)).then(
      (response) => {
        if (datasetId) {
          dispatch(setFormValue({ id: "ImportFrom", value: "fromfile" }));
          dispatch(setFormValue({ id: "SelectedDataset", value: datasetId }));
          dispatch(setPageValue({ id: "currentStepIndex", value: 1 }));
          dispatch(setPageValue({ id: "previousStepIndex", value: 0 }));
          dispatch(setPageValue({ id: "nextStepIndex", value: 2 }));
          dispatch(setPageValue({ id: "steps", value: fromFileSteps }));
          dispatch(setPageValue({ id: "stepPages", value: stepFilePages }));
        }
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.importdataset.unknown_error1"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.importdataset.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*toolbar*/
  const handleOnNew = (e) => { 
    if (!currentUser.IsAuthenticated || dataSourceMaxReached(currentUser)) {
      dispatch(
        addNotification({
          text: t("datasets.importdataset.error"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
      return;
    }

    if (blockNavigationIfDirty() == true) return;

    const searchParams = new URLSearchParams(location.search);
    const datasetId = searchParams.get("datasetid");
    dispatch(loadPage(t)).then(
      (response) => {
        if (datasetId) {
          dispatch(setFormValue({ id: "ImportFrom", value: "fromfile" }));
          dispatch(setFormValue({ id: "SelectedDataset", value: datasetId }));
          dispatch(setPageValue({ id: "currentStepIndex", value: 1 }));
          dispatch(setPageValue({ id: "previousStepIndex", value: 0 }));
          dispatch(setPageValue({ id: "nextStepIndex", value: 2 }));
          dispatch(setPageValue({ id: "steps", value: fromFileSteps }));
          dispatch(setPageValue({ id: "stepPages", value: stepFilePages }));
        }
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.importdataset.error1"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.importdataset.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  const handleOnClose = (e) => {
    if (blockNavigationIfDirty() == true) return;
    navigate("/datasets");
  };

  //tools visible/hidden
  const handleOnToolsToggle = (e) => { 
    const isVisible = pageState.toolsVisible;

    if (isVisible) {
      //hide
      dispatch(
        setPanes([
          {
            size: "0%",
            min: "%",
            max: "%",
            collapsible: false,
          },
          {
            min: "100%",
            collapsible: false,
          },
        ])
      );
    } else {
      dispatch(
        setPanes([
          {
            size: "20%",
            min: "20%",
            max: "25%",
            collapsible: false,
          },
          {
            min: "70%",
            max: "80%",
            collapsible: false,
          },
        ])
      );
    }

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
  };

  useEffect(() => {
    const fromDbSteps = [
      { label: t("datasets.importdataset.from"), isValid: true },
      { label: t("datasets.importdataset.fromdb"), isValid: undefined },
      { label: t("datasets.importdataset.tablequery"), isValid: undefined },
      { label: t("datasets.importdataset.review"), isValid: undefined },
      { label: t("datasets.importdataset.result"), isValid: undefined },
    ];
    dispatch(setPageValue({ id: "steps", value: fromDbSteps }));
  }, [i18n.language]);
  /*toolbar*/

  return (
    <Fragment>
      <Page
        id="datasetsPage"
        className="datasets-page"
        title={t("datasets.importdataset.dataset")}
        animation={props.animation}
      >
        <ImportDatasetPageBody
          onNew={handleOnNew}
          onClose={handleOnClose}
          updateDirtyState={updateDirtyState}
          onSplitterChange={handleOnSplitterChange}
          onToolsToggle={handleOnToolsToggle}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .datasets-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .datasets-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .datasets-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .datasets-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .datasets-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    `}
      </style>
    </Fragment>
  );
};
